import type { Plot } from '@/types';

export function landClaimBlockReason(input: {
  phase?: string;
  highlightedPlotId: string | null;
  previousPlotId?: string | null;
  tileSequence?: string[];
  recentOffers?: Record<string, number> | null;
  plot: Pick<Plot, 'plotId' | 'terrain'>;
  alreadyClaimed: boolean;
  countdown?: number | null;
}): string | null {
  if (input.phase !== 'land_claim') {
    return 'Land claiming is only available during the land claim phase.';
  }
  if (input.countdown) {
    return 'Get ready';
  }
  if (!input.plot.plotId) {
    return 'That plot cannot be claimed.';
  }
  const sequence = input.tileSequence || [];
  const highlightIndex = input.highlightedPlotId ? sequence.indexOf(input.highlightedPlotId) : -1;
  const tappedIndex = sequence.indexOf(input.plot.plotId);
  const offerUntil = input.recentOffers?.[input.plot.plotId];
  const offerOpen = Number.isFinite(offerUntil) && Date.now() <= Number(offerUntil);
  const recentlyShown = highlightIndex >= 0 && tappedIndex >= 0
    && highlightIndex - tappedIndex >= 1
    && highlightIndex - tappedIndex <= 5;
  if (
    input.highlightedPlotId
    && input.plot.plotId !== input.highlightedPlotId
    && input.plot.plotId !== input.previousPlotId
    && !recentlyShown
    && !offerOpen
  ) {
    return 'Only the highlighted plot can be claimed! Wait for it to be highlighted.';
  }
  if (input.plot.terrain === 'base') {
    return 'Cannot claim the base tile.';
  }
  if (input.alreadyClaimed) {
    return 'You have already claimed a tile in this land claim phase. Wait for the next phase.';
  }
  return null;
}

export function isClaimConflict(message: string): boolean {
  return /conflict/i.test(message);
}

/** A missing game leaves the board. A conflict stays on the board. */
export function shouldReturnToLobby(message: string): boolean {
  return /not found/i.test(message) && !isClaimConflict(message);
}

export async function claimWithFreshCopy<T>(
  submit: (etag?: string) => Promise<T>,
  refresh: () => Promise<string | undefined>,
  etag: string | undefined,
  failed: (result: T) => string | null,
): Promise<T> {
  const first = await submit(etag);
  const message = failed(first);
  if (!message || !isClaimConflict(message)) return first;
  const nextEtag = await refresh();
  return submit(nextEtag);
}
